import { FC, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Alert,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Assessment as AssessmentIcon } from '@mui/icons-material';
import TruckDailyReportDialog from './TruckDailyReportDialog';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import api from '../utils/api';
import { useRBAC } from '../auth/useRBAC';
import { withOfflineFallback, readVeterinarians, OfflineNotice, useIsOffline } from '../offline';
import {
  inventoryService,
  StockLocation,
  StockLocationType,
  CreateLocationPayload,
  UpdateLocationPayload,
} from '../services/inventoryService';

// Mirrors backend UsersService.getVeterinariansByClinic's select shape
// (GET /users/veterinarians) — precedent: ControlledSubstanceAdminDialog's
// witness picker + AddAppointmentDialog's vet picker.
interface Veterinarian {
  id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  professionalTitle?: string;
}

const TYPE_LABELS: Record<StockLocationType, string> = {
  WAREHOUSE: 'Warehouse',
  TRUCK: 'Truck',
  SITE: 'Site',
};

const TYPE_COLORS: Record<StockLocationType, 'default' | 'info' | 'secondary'> = {
  WAREHOUSE: 'default',
  TRUCK: 'info',
  SITE: 'secondary',
};

interface LocationFormData {
  name: string;
  type: StockLocationType;
  ownerVetId: string; // '' = unassigned
  isActive: boolean;
}

const initialFormData: LocationFormData = {
  name: '',
  type: 'TRUCK',
  ownerVetId: '',
  isActive: true,
};

/**
 * Locations tab (Phase 3, trucks): manage the clinic's StockLocations — the
 * default warehouse (seeded, locked), any extra warehouses/sites, and field
 * trucks assigned to a vet. Distinct from the inventory STOCK list (which
 * tracks items *at* a location) — this manages the locations themselves.
 *
 * Manager-gated (manage:inventory) + online-only, same convention as
 * Settings/ServicePricing.tsx: location writes are direct `api` calls (never
 * the offline write queue), so both the Add button and each row's Edit
 * action are disabled offline even for a manager.
 */
const LocationsPanel: FC = () => {
  const { hasPermission } = useRBAC();
  const canManage = hasPermission('manage:inventory');
  const isOffline = useIsOffline();
  const canWrite = canManage && !isOffline;

  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { data: locations = [], isLoading } = useQuery<StockLocation[]>(
    ['stock-locations'],
    () => inventoryService.getLocations(),
  );

  // Owner-vet picker + name resolution for existing TRUCK rows. Offline-aware
  // (mirrors AddAppointmentDialog's 'veterinarians' query) so the list still
  // shows owner names when offline — only the write actions are online-only.
  const { data: veterinarians = [] } = useQuery<Veterinarian[]>(
    'veterinarians',
    withOfflineFallback(
      async () => {
        const response = await api.get('/users/veterinarians');
        return response.data;
      },
      () => readVeterinarians() as Promise<Veterinarian[]>,
    ),
  );

  const vetNameById = useMemo(() => {
    const map = new Map<string, string>();
    veterinarians.forEach((v) => map.set(v.id, v.name));
    return map;
  }, [veterinarians]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<StockLocation | null>(null);
  const [formData, setFormData] = useState<LocationFormData>(initialFormData);
  // Daily report (the truck count-sheet ritual) — per-row action, view:inventory.
  const [reportLocation, setReportLocation] = useState<StockLocation | null>(null);

  const invalidateAfterWrite = () => {
    queryClient.invalidateQueries(['stock-locations']);
    // Locations feed the Inventory page's location filter/picker + row labels.
    queryClient.invalidateQueries(['inventory']);
  };

  const createMutation = useMutation(
    (payload: CreateLocationPayload) => inventoryService.createLocation(payload),
    {
      onSuccess: () => {
        invalidateAfterWrite();
        enqueueSnackbar('Location created', { variant: 'success' });
        handleCloseDialog();
      },
      onError: (error: any) => {
        enqueueSnackbar(error.response?.data?.message || 'Failed to create location', { variant: 'error' });
      },
    },
  );

  const updateMutation = useMutation(
    ({ id, payload }: { id: string; payload: UpdateLocationPayload }) =>
      inventoryService.updateLocation(id, payload),
    {
      onSuccess: () => {
        invalidateAfterWrite();
        enqueueSnackbar('Location updated', { variant: 'success' });
        handleCloseDialog();
      },
      onError: (error: any) => {
        enqueueSnackbar(error.response?.data?.message || 'Failed to update location', { variant: 'error' });
      },
    },
  );

  const handleOpenAdd = () => {
    setEditingLocation(null);
    setFormData(initialFormData);
    setDialogOpen(true);
  };

  const handleOpenEdit = (loc: StockLocation) => {
    // The default warehouse never offers an edit action (see the row render
    // below) — this is only reachable for non-default locations.
    setEditingLocation(loc);
    setFormData({
      name: loc.name,
      type: loc.type,
      ownerVetId: loc.ownerVetId ?? '',
      isActive: loc.isActive,
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingLocation(null);
    setFormData(initialFormData);
  };

  const handleSubmit = () => {
    const name = formData.name.trim();
    if (!name) return;

    if (editingLocation) {
      const payload: UpdateLocationPayload = {
        name,
        type: formData.type,
        isActive: formData.isActive,
      };
      // Only send ownerVetId while it's a TRUCK — omitted (not cleared) when
      // retyping away from TRUCK, since the field is never displayed/used for
      // WAREHOUSE/SITE rows; clearing the stale id server-side is a follow-up.
      if (formData.type === 'TRUCK' && formData.ownerVetId) {
        payload.ownerVetId = formData.ownerVetId;
      }
      updateMutation.mutate({ id: editingLocation.id, payload });
    } else {
      const payload: CreateLocationPayload = {
        name,
        type: formData.type,
      };
      if (formData.type === 'TRUCK' && formData.ownerVetId) {
        payload.ownerVetId = formData.ownerVetId;
      }
      createMutation.mutate(payload);
    }
  };

  const isSaving = createMutation.isLoading || updateMutation.isLoading;
  const isValid = formData.name.trim().length > 0;

  return (
    <Box sx={{ mb: 3 }}>
      <OfflineNotice>
        You're offline — location changes need a connection. Existing locations still show below.
      </OfflineNotice>

      {!canManage && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Read-only — you don't have permission to manage locations.
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 1 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ flexGrow: 1 }}>
          Locations
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAdd}
          disabled={!canWrite}
        >
          Add location
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The warehouse is where stock lives by default. Add a truck and assign it to a vet so field
        dispenses can be tracked against the right location.
      </Typography>

      {isLoading ? (
        <CircularProgress />
      ) : (
        <Paper variant="outlined">
          {locations.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">No locations yet.</Typography>
            </Box>
          ) : (
            locations.map((loc, i) => (
              <Box
                key={loc.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 2,
                  borderTop: i ? '1px solid' : 'none',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography fontWeight={600}>{loc.name}</Typography>
                    <Chip size="small" label={TYPE_LABELS[loc.type]} color={TYPE_COLORS[loc.type]} />
                    {loc.isDefault && (
                      <Chip size="small" variant="outlined" label="Default warehouse" />
                    )}
                    <Chip
                      size="small"
                      variant="outlined"
                      color={loc.isActive ? 'success' : 'default'}
                      label={loc.isActive ? 'Active' : 'Inactive'}
                    />
                  </Box>
                  {loc.type === 'TRUCK' && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Owner: {loc.ownerVetId ? (vetNameById.get(loc.ownerVetId) ?? 'Unknown vet') : 'Unassigned'}
                    </Typography>
                  )}
                </Box>
                <Tooltip title={isOffline ? 'Daily report needs a connection' : 'Daily report'}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => setReportLocation(loc)}
                      disabled={isOffline}
                      sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                    >
                      <AssessmentIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                {loc.isDefault ? (
                  <Tooltip title="The default warehouse can't be edited">
                    <span>
                      <IconButton size="small" disabled>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : (
                  <IconButton
                    size="small"
                    onClick={() => handleOpenEdit(loc)}
                    disabled={!canWrite}
                    sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))
          )}
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{editingLocation ? 'Edit location' : 'Add location'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              autoFocus
              fullWidth
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g. Dr. Ramirez's truck"
              required
            />
            <TextField
              select
              fullWidth
              label="Type"
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as StockLocationType })}
            >
              <MenuItem value="WAREHOUSE">Warehouse</MenuItem>
              <MenuItem value="TRUCK">Truck</MenuItem>
              <MenuItem value="SITE">Site</MenuItem>
            </TextField>

            {formData.type === 'TRUCK' && (
              <TextField
                select
                fullWidth
                label="Owner vet"
                value={formData.ownerVetId}
                onChange={(e) => setFormData({ ...formData, ownerVetId: e.target.value })}
                helperText="The vet this truck is assigned to (optional)"
              >
                <MenuItem value="">
                  <em>Unassigned</em>
                </MenuItem>
                {veterinarians.map((vet) => (
                  <MenuItem key={vet.id} value={vet.id}>
                    {vet.name}
                  </MenuItem>
                ))}
              </TextField>
            )}

            {editingLocation && (
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={!isValid || isSaving}>
            {editingLocation ? 'Save changes' : 'Add location'}
          </Button>
        </DialogActions>
      </Dialog>

      <TruckDailyReportDialog
        open={!!reportLocation}
        onClose={() => setReportLocation(null)}
        location={reportLocation}
      />
    </Box>
  );
};

export default LocationsPanel;
